// AdminReconciliation.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { bookingService } from '../services/bookingService';
import { adminService } from '../services/adminService';
import { sectionService } from '../services/sectionService';
import './AdminReconciliation.css';

// Chart imports - Recharts (MIT licensed)
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    AreaChart, Area, ComposedChart
} from 'recharts';

const AdminReconciliation = ({ addNotification }) => {
    // ============================================
    // EXISTING STATES
    // ============================================
    const [pendingBookings, setPendingBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showIssueModal, setShowIssueModal] = useState(false);
    const [availableEquipment, setAvailableEquipment] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [issuing, setIssuing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(6);

    // ============================================
    // DYNAMIC REPORT STATES
    // ============================================
    const [activeTab, setActiveTab] = useState('pending');
    const [reportLoading, setReportLoading] = useState(false);
    const [availableYears, setAvailableYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableQuarters, setAvailableQuarters] = useState([1, 2, 3, 4]);
    const [selectedQuarter, setSelectedQuarter] = useState(1);
    const [availableMonths, setAvailableMonths] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    
    const [dateRange, setDateRange] = useState({
        minDate: null,
        maxDate: null,
        startDate: null,
        endDate: null
    });
    
    const [monthlyData, setMonthlyData] = useState([]);
    const [quarterlyData, setQuarterlyData] = useState([]);
    const [yearlyData, setYearlyData] = useState([]);
    const [sectionMemberships, setSectionMemberships] = useState([]);
    const [sectionRequests, setSectionRequests] = useState([]);
    const [sectionStats, setSectionStats] = useState([]);
    
    const [reconciliationStats, setReconciliationStats] = useState({
        total: 0,
        completed: 0,
        pending: 0,
        itemsReturned: 0,
        itemsIssued: 0,
        onTimeReturns: 0,
        lateReturns: 0,
        avgProcessingDays: 0
    });
    
    const [equipmentStats, setEquipmentStats] = useState([]);
    const [allReconciliations, setAllReconciliations] = useState([]);
    const [dataTablePage, setDataTablePage] = useState(1);
    const [dataTableRowsPerPage, setDataTableRowsPerPage] = useState(10);

    const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

    // ============================================
    // FETCH FUNCTIONS
    // ============================================
    
    const fetchPendingReconciliations = async () => {
        setLoading(true);
        try {
            const response = await bookingService.getPendingReconciliations();
            if (response.success) {
                setPendingBookings(response.bookings || []);
                setCurrentPage(1);
            } else {
                setPendingBookings([]);
                if (addNotification) addNotification(response.message || 'Failed to load pending reconciliations', 'warning');
            }
        } catch (error) {
            console.error('Error fetching pending reconciliations:', error);
            if (addNotification) addNotification('Failed to load pending reconciliations', 'error');
            setPendingBookings([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableEquipment = async () => {
        try {
            const response = await bookingService.getEquipment();
            if (response.success) {
                setAvailableEquipment(response.equipment || []);
            }
        } catch (error) {
            console.error('Error fetching equipment:', error);
        }
    };

    const fetchAvailableYears = useCallback(async () => {
        try {
            const response = await adminService.getAvailableYears();
            if (response.success && response.years && response.years.length > 0) {
                setAvailableYears(response.years);
                setSelectedYear(Math.max(...response.years));
            }
        } catch (error) {
            console.error('Error fetching available years:', error);
            const currentYear = new Date().getFullYear();
            setAvailableYears([currentYear - 2, currentYear - 1, currentYear]);
            setSelectedYear(currentYear);
        }
    }, []);

    const fetchDateRange = useCallback(async () => {
        try {
            const response = await adminService.getDateRange();
            if (response.success && response.minDate) {
                setDateRange({
                    minDate: response.minDate,
                    maxDate: response.maxDate,
                    startDate: response.minDate,
                    endDate: response.maxDate
                });
            }
        } catch (error) {
            console.error('Error fetching date range:', error);
        }
    }, []);

    const fetchSectionData = useCallback(async () => {
        try {
            const [membershipsResponse, requestsResponse, sectionsResponse] = await Promise.all([
                sectionService.getMyMemberships(),
                sectionService.getPendingRequests('pending'),
                adminService.getAllSectionsWithCounts()
            ]);

            if (membershipsResponse?.success && membershipsResponse.memberships) {
                setSectionMemberships(membershipsResponse.memberships);
            }
            if (requestsResponse?.success && requestsResponse.requests) {
                setSectionRequests(requestsResponse.requests);
            }
            if (sectionsResponse?.success && sectionsResponse.sections) {
                setSectionStats(sectionsResponse.sections);
            }
        } catch (error) {
            console.error('Error fetching section data:', error);
        }
    }, []);

    const fetchAllReportData = useCallback(async () => {
        setReportLoading(true);
        try {
            const [allBookingsResponse, equipmentStatsResponse] = await Promise.all([
                adminService.getAllBookings({ limit: 5000 }),
                adminService.getEquipmentStats()
            ]);

            if (allBookingsResponse?.success && allBookingsResponse.bookings) {
                const bookings = allBookingsResponse.bookings;
                setAllReconciliations(bookings);
                
                const completedRecon = bookings.filter(b => b.reconciliation_status === 'complete');
                const pendingRecon = bookings.filter(b => b.reconciliation_status === 'pending');
                
                setReconciliationStats({
                    total: bookings.length,
                    completed: completedRecon.length,
                    pending: pendingRecon.length,
                    itemsReturned: 0,
                    itemsIssued: 0,
                    onTimeReturns: 0,
                    lateReturns: 0,
                    avgProcessingDays: 0
                });
            }

            if (equipmentStatsResponse?.success && equipmentStatsResponse.stats) {
                setEquipmentStats(equipmentStatsResponse.stats);
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            if (addNotification) addNotification('Failed to load report data', 'error');
        } finally {
            setReportLoading(false);
        }
    }, [addNotification]);

    // FIXED: Use from_date and to_date (backend expects these)
    const generateMonthlyData = useCallback(async () => {
        if (!selectedYear) return;
        setReportLoading(true);
        try {
            const months = [];
            for (let month = 1; month <= 12; month++) {
                const startDate = new Date(selectedYear, month - 1, 1);
                const endDate = new Date(selectedYear, month, 0);
                
                const startDateStr = startDate.toISOString().split('T')[0];
                const endDateStr = endDate.toISOString().split('T')[0];
                
                // FIX: Use from_date and to_date instead of start_date and end_date
                const response = await adminService.getAllBookings({ 
                    limit: 1000,
                    from_date: startDateStr,
                    to_date: endDateStr
                });
                
                if (response?.success && response.bookings) {
                    const monthBookings = response.bookings;
                    const completed = monthBookings.filter(b => b.reconciliation_status === 'complete').length;
                    const pending = monthBookings.filter(b => b.reconciliation_status === 'pending').length;
                    const totalPending = monthBookings.reduce((sum, b) => sum + (parseInt(b.total_pending) || 0), 0);
                    
                    months.push({
                        month: month,
                        label: startDate.toLocaleString('default', { month: 'long' }),
                        reconciliations: monthBookings.length,
                        completed: completed,
                        pending: pending,
                        itemsIssued: totalPending,
                        itemsReturned: 0,
                        onTimeRate: monthBookings.length > 0 ? Math.round((completed / monthBookings.length) * 100) : 0,
                        completionRate: monthBookings.length > 0 ? Math.round((completed / monthBookings.length) * 100) : 0
                    });
                } else {
                    months.push({
                        month: month,
                        label: startDate.toLocaleString('default', { month: 'long' }),
                        reconciliations: 0,
                        completed: 0,
                        pending: 0,
                        itemsIssued: 0,
                        itemsReturned: 0,
                        onTimeRate: 0,
                        completionRate: 0
                    });
                }
            }
            setMonthlyData(months);
        } catch (error) {
            console.error('Error generating monthly data:', error);
        } finally {
            setReportLoading(false);
        }
    }, [selectedYear]);

    // FIXED: Use from_date and to_date
    const generateQuarterlyData = useCallback(async () => {
        if (!selectedYear) return;
        setReportLoading(true);
        try {
            const quarters = [];
            for (let quarter = 1; quarter <= 4; quarter++) {
                const startMonth = (quarter - 1) * 3;
                const startDate = new Date(selectedYear, startMonth, 1);
                const endDate = new Date(selectedYear, startMonth + 3, 0);
                
                const startDateStr = startDate.toISOString().split('T')[0];
                const endDateStr = endDate.toISOString().split('T')[0];
                
                // FIX: Use from_date and to_date
                const response = await adminService.getAllBookings({ 
                    limit: 1000,
                    from_date: startDateStr,
                    to_date: endDateStr
                });
                
                if (response?.success && response.bookings) {
                    const quarterBookings = response.bookings;
                    const completed = quarterBookings.filter(b => b.reconciliation_status === 'complete').length;
                    const pending = quarterBookings.filter(b => b.reconciliation_status === 'pending').length;
                    const totalPending = quarterBookings.reduce((sum, b) => sum + (parseInt(b.total_pending) || 0), 0);
                    
                    quarters.push({
                        quarter: quarter,
                        label: `Q${quarter} ${selectedYear}`,
                        reconciliations: quarterBookings.length,
                        completed: completed,
                        pending: pending,
                        itemsIssued: totalPending,
                        itemsReturned: 0,
                        completionRate: quarterBookings.length > 0 ? Math.round((completed / quarterBookings.length) * 100) : 0
                    });
                } else {
                    quarters.push({
                        quarter: quarter,
                        label: `Q${quarter} ${selectedYear}`,
                        reconciliations: 0,
                        completed: 0,
                        pending: 0,
                        itemsIssued: 0,
                        itemsReturned: 0,
                        completionRate: 0
                    });
                }
            }
            setQuarterlyData(quarters);
        } catch (error) {
            console.error('Error generating quarterly data:', error);
        } finally {
            setReportLoading(false);
        }
    }, [selectedYear]);

    // FIXED: Use from_date and to_date
    const generateYearlyData = useCallback(async () => {
        setReportLoading(true);
        try {
            const years = [];
            const currentYear = new Date().getFullYear();
            const startYear = currentYear - 4;
            
            for (let year = startYear; year <= currentYear; year++) {
                const startDate = new Date(year, 0, 1);
                const endDate = new Date(year, 11, 31);
                
                const startDateStr = startDate.toISOString().split('T')[0];
                const endDateStr = endDate.toISOString().split('T')[0];
                
                // FIX: Use from_date and to_date
                const response = await adminService.getAllBookings({ 
                    limit: 5000,
                    from_date: startDateStr,
                    to_date: endDateStr
                });
                
                if (response?.success && response.bookings) {
                    const yearBookings = response.bookings;
                    const completed = yearBookings.filter(b => b.reconciliation_status === 'complete').length;
                    const pending = yearBookings.filter(b => b.reconciliation_status === 'pending').length;
                    const totalPending = yearBookings.reduce((sum, b) => sum + (parseInt(b.total_pending) || 0), 0);
                    
                    const prevYear = years[years.length - 1];
                    const growth = prevYear && prevYear.reconciliations > 0 
                        ? ((yearBookings.length - prevYear.reconciliations) / prevYear.reconciliations * 100).toFixed(1) 
                        : 0;
                    
                    years.push({
                        year: year,
                        reconciliations: yearBookings.length,
                        completed: completed,
                        pending: pending,
                        itemsIssued: totalPending,
                        itemsReturned: 0,
                        growth: parseFloat(growth)
                    });
                } else {
                    years.push({
                        year: year,
                        reconciliations: 0,
                        completed: 0,
                        pending: 0,
                        itemsIssued: 0,
                        itemsReturned: 0,
                        growth: 0
                    });
                }
            }
            setYearlyData(years);
        } catch (error) {
            console.error('Error generating yearly data:', error);
        } finally {
            setReportLoading(false);
        }
    }, []);

    // ============================================
    // HANDLERS
    // ============================================
    const handleIssueItems = async () => {
        if (selectedItems.length === 0) {
            if (addNotification) addNotification('Please select items to issue', 'warning');
            return;
        }

        setIssuing(true);
        try {
            const response = await bookingService.issueItems(selectedBooking.id, selectedItems);
            if (response.success) {
                if (addNotification) addNotification(response.message, 'success');
                setShowIssueModal(false);
                setSelectedBooking(null);
                setSelectedItems([]);
                fetchPendingReconciliations();
                fetchAllReportData();
            } else {
                if (addNotification) addNotification(response.message || 'Failed to issue items', 'error');
            }
        } catch (error) {
            console.error('Error issuing items:', error);
            if (addNotification) addNotification('An error occurred. Please try again.', 'error');
        } finally {
            setIssuing(false);
        }
    };

const handleCompleteReconciliation = async (bookingId) => {
    console.log('Completing reconciliation for booking:', bookingId);
    const notes = prompt('Enter notes for completing this reconciliation:');
    if (notes === null) return;
    
    try {
        console.log('Sending request with notes:', notes);
        const response = await bookingService.completeReconciliation(bookingId, notes);
        console.log('Response from server:', response);
        
        if (response.success) {
            if (addNotification) addNotification(response.message, 'success');
            fetchPendingReconciliations();
            fetchAllReportData();
            
            // Fetch the updated booking to see the reconciled_at value
            const updatedBooking = await adminService.getAllBookings({ limit: 1 });
            console.log('Updated bookings:', updatedBooking);
        } else {
            if (addNotification) addNotification(response.message || 'Failed to complete reconciliation', 'error');
        }
    } catch (error) {
        console.error('Error completing reconciliation:', error);
        if (addNotification) addNotification('An error occurred. Please try again.', 'error');
    }
};

    // ============================================
    // EFFECTS
    // ============================================
    useEffect(() => {
        fetchPendingReconciliations();
        fetchAvailableEquipment();
        fetchAvailableYears();
        fetchDateRange();
        fetchAllReportData();
        fetchSectionData();
    }, []);

    useEffect(() => {
        if (activeTab === 'monthly') {
            generateMonthlyData();
        } else if (activeTab === 'quarterly') {
            generateQuarterlyData();
        } else if (activeTab === 'yearly') {
            generateYearlyData();
        }
    }, [activeTab, selectedYear, generateMonthlyData, generateQuarterlyData, generateYearlyData]);

    // ============================================
    // PAGINATION HELPERS
    // ============================================
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentBookings = pendingBookings.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(pendingBookings.length / itemsPerPage);

    const dataTableStart = (dataTablePage - 1) * dataTableRowsPerPage;
    const currentTableData = allReconciliations.slice(dataTableStart, dataTableStart + dataTableRowsPerPage);
    const totalTablePages = Math.ceil(allReconciliations.length / dataTableRowsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const convertToCSV = (data) => {
        if (!data || data.length === 0) return '';
        const headers = Object.keys(data[0]);
        const csvRows = [headers.join(',')];
        for (const row of data) {
            const values = headers.map(header => {
                const val = row[header] || '';
                return `"${String(val).replace(/"/g, '""')}"`;
            });
            csvRows.push(values.join(','));
        }
        return csvRows.join('\n');
    };

    const downloadCSV = (csv, filename) => {
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const downloadJSON = (json, filename) => {
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ============================================
    // RENDER COMPONENTS
    // ============================================
    
    const StatisticsCards = () => (
        <div className="stats-grid">
            <div className="stat-card">
                <div className="stat-icon green">
                    <i className="fa-solid fa-check-circle"></i>
                </div>
                <div className="stat-info">
                    <h3>{reconciliationStats.completed}</h3>
                    <p>Completed Reconciliations</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon yellow">
                    <i className="fa-solid fa-clock"></i>
                </div>
                <div className="stat-info">
                    <h3>{reconciliationStats.pending}</h3>
                    <p>Pending Reconciliations</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon blue">
                    <i className="fa-solid fa-arrow-right-arrow-left"></i>
                </div>
                <div className="stat-info">
                    <h3>{reconciliationStats.itemsIssued}</h3>
                    <p>Items Issued</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon purple">
                    <i className="fa-solid fa-arrow-left"></i>
                </div>
                <div className="stat-info">
                    <h3>{reconciliationStats.itemsReturned}</h3>
                    <p>Items Returned</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon orange">
                    <i className="fa-solid fa-calendar-check"></i>
                </div>
                <div className="stat-info">
                    <h3>{reconciliationStats.onTimeReturns}</h3>
                    <p>On-Time Returns</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon red">
                    <i className="fa-solid fa-calendar-times"></i>
                </div>
                <div className="stat-info">
                    <h3>{reconciliationStats.lateReturns}</h3>
                    <p>Late Returns</p>
                </div>
            </div>
        </div>
    );

    const SectionStatsCards = () => (
        <div className="stats-grid">
            <div className="stat-card">
                <div className="stat-icon purple">
                    <i className="fa-solid fa-users"></i>
                </div>
                <div className="stat-info">
                    <h3>{sectionStats.length}</h3>
                    <p>Total Sections</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon blue">
                    <i className="fa-solid fa-user-plus"></i>
                </div>
                <div className="stat-info">
                    <h3>{sectionRequests.length}</h3>
                    <p>Pending Section Requests</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon green">
                    <i className="fa-solid fa-user-check"></i>
                </div>
                <div className="stat-info">
                    <h3>{sectionMemberships.length}</h3>
                    <p>Your Memberships</p>
                </div>
            </div>
        </div>
    );

    const ReconciliationTrendChart = ({ data, title, color }) => (
        <div className="chart-container">
            <h3>{title}</h3>
            <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="reconciliations" fill={color || "#3B82F6"} name="Total Reconciliations" />
                    <Bar yAxisId="left" dataKey="completed" fill="#10B981" name="Completed" />
                    <Line yAxisId="right" type="monotone" dataKey="completionRate" stroke="#F59E0B" name="Completion Rate (%)" />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );

    const ItemsFlowChart = ({ data }) => (
        <div className="chart-container">
            <h3>Items Flow (Issued vs Returned)</h3>
            <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="itemsIssued" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Items Issued" />
                    <Area type="monotone" dataKey="itemsReturned" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Items Returned" />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );

    const CompletionRatePieChart = () => {
        const pieData = [
            { name: 'Completed', value: reconciliationStats.completed, color: '#10B981' },
            { name: 'Pending', value: reconciliationStats.pending, color: '#F59E0B' }
        ];
        
        return (
            <div className="chart-container">
                <h3>Reconciliation Status</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {pieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const EquipmentStatsChart = () => (
        <div className="chart-container">
            <h3>Equipment Usage Statistics</h3>
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={equipmentStats.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={100} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="times_issued" fill="#3B82F6" name="Times Issued" />
                    <Bar dataKey="times_returned" fill="#10B981" name="Times Returned" />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    const SectionMembershipChart = () => (
        <div className="chart-container">
            <h3>Section Memberships Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sectionStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="member_count" fill="#8B5CF6" name="Members" />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    const ReconciliationDataTable = () => (
        <div className="data-table-container">
            <div className="table-header">
                <h3>Reconciliation History</h3>
                <div className="table-controls">
                    <select 
                        value={dataTableRowsPerPage} 
                        onChange={(e) => setDataTableRowsPerPage(Number(e.target.value))}
                        className="rows-select"
                    >
                        <option value={10}>10 rows</option>
                        <option value={25}>25 rows</option>
                        <option value={50}>50 rows</option>
                        <option value={100}>100 rows</option>
                    </select>
                    {dateRange.minDate && dateRange.maxDate && (
                        <div className="date-range-info">
                            <i className="fa-solid fa-calendar"></i>
                            Data: {new Date(dateRange.minDate).toLocaleDateString()} - {new Date(dateRange.maxDate).toLocaleDateString()}
                        </div>
                    )}
                </div>
            </div>
            
            <div className="table-responsive">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Booking #</th>
                            <th>Member</th>
                            <th>Facility</th>
                            <th>Booking Date</th>
                            <th>Status</th>
                            <th>Reconciliation</th>
                            <th>Completion Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentTableData.map((booking) => (
                            <tr key={booking.id}>
                                <td>#{booking.booking_number}</td>
                                <td>{booking.full_name || booking.member_full_name || `${booking.first_name} ${booking.last_name}`}</td>
                                <td>{booking.facility_name || booking.location || 'N/A'}</td>
                                <td>{booking.booking_date ? new Date(booking.booking_date).toLocaleDateString() : 'N/A'}</td>
                                <td>
                                    <span className={`status-badge ${booking.status === 'approved' ? 'approved' : booking.status}`}>
                                        {booking.status || 'N/A'}
                                    </span>
                                </td>
                                <td>
                                    <span className={`status-badge ${booking.reconciliation_status === 'complete' ? 'completed' : 'pending'}`}>
                                        {booking.reconciliation_status === 'complete' ? 'Complete' : 'Pending'}
                                    </span>
                                </td>
                                <td>{booking.reconciled_at ? new Date(booking.reconciled_at).toLocaleDateString() : '-'}</td>
                                <td>
                                    {booking.reconciliation_status !== 'complete' && (
                                        <button 
                                            className="action-btn complete-btn"
                                            onClick={() => handleCompleteReconciliation(booking.id)}
                                            title="Complete Reconciliation"
                                        >
                                            <i className="fa-solid fa-check"></i>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {currentTableData.length === 0 && (
                            <tr>
                                <td colSpan="8" className="text-center">No reconciliation records found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {allReconciliations.length > dataTableRowsPerPage && (
                <div className="pagination">
                    <button 
                        onClick={() => setDataTablePage(p => Math.max(1, p - 1))}
                        disabled={dataTablePage === 1}
                    >
                        <i className="fa-solid fa-chevron-left"></i> Previous
                    </button>
                    <span>Page {dataTablePage} of {totalTablePages}</span>
                    <button 
                        onClick={() => setDataTablePage(p => Math.min(totalTablePages, p + 1))}
                        disabled={dataTablePage === totalTablePages}
                    >
                        Next <i className="fa-solid fa-chevron-right"></i>
                    </button>
                </div>
            )}
        </div>
    );

    const YearSelector = () => (
        <div className="year-selector">
            <label>Select Year:</label>
            <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
                {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                ))}
            </select>
        </div>
    );

    const QuarterSelector = () => (
        <div className="quarter-selector">
            <label>Select Quarter:</label>
            <select 
                value={selectedQuarter} 
                onChange={(e) => setSelectedQuarter(Number(e.target.value))}
            >
                {availableQuarters.map(quarter => (
                    <option key={quarter} value={quarter}>Q{quarter}</option>
                ))}
            </select>
        </div>
    );

    const MonthSelector = () => (
        <div className="month-selector">
            <label>Select Month:</label>
            <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
                {availableMonths.map(month => {
                    const date = new Date(2000, month - 1, 1);
                    return (
                        <option key={month} value={month}>
                            {date.toLocaleString('default', { month: 'long' })}
                        </option>
                    );
                })}
            </select>
        </div>
    );

    const TabNavigation = () => (
        <div className="tab-navigation">
            <button className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
                <i className="fa-solid fa-clock"></i> Pending Reconciliations
                {pendingBookings.length > 0 && <span className="tab-badge">{pendingBookings.length}</span>}
            </button>
            <button className={`tab-btn ${activeTab === 'monthly' ? 'active' : ''}`} onClick={() => setActiveTab('monthly')}>
                <i className="fa-solid fa-calendar-alt"></i> Monthly Report
            </button>
            <button className={`tab-btn ${activeTab === 'quarterly' ? 'active' : ''}`} onClick={() => setActiveTab('quarterly')}>
                <i className="fa-solid fa-chart-line"></i> Quarterly Report
            </button>
            <button className={`tab-btn ${activeTab === 'yearly' ? 'active' : ''}`} onClick={() => setActiveTab('yearly')}>
                <i className="fa-solid fa-chart-bar"></i> Yearly Report
            </button>
            <button className={`tab-btn ${activeTab === 'statistics' ? 'active' : ''}`} onClick={() => setActiveTab('statistics')}>
                <i className="fa-solid fa-chart-pie"></i> Statistics
            </button>
            <button className={`tab-btn ${activeTab === 'sections' ? 'active' : ''}`} onClick={() => setActiveTab('sections')}>
                <i className="fa-solid fa-layer-group"></i> Sections
            </button>
        </div>
    );

    const PendingReconciliationsTab = () => (
        <>
            {pendingBookings.length > 0 && (
                <div className="stats-grid">
                    <div className="stat-box">
                        <div className="stat-icon green">
                            <i className="fa-solid fa-clock"></i>
                        </div>
                        <div>
                            <div className="stat-number">{pendingBookings.length}</div>
                            <div className="stat-label">Pending Reconciliations</div>
                        </div>
                    </div>
                    <div className="stat-box">
                        <div className="stat-icon blue">
                            <i className="fa-solid fa-cube"></i>
                        </div>
                        <div>
                            <div className="stat-number">{pendingBookings.reduce((sum, b) => sum + (parseInt(b.pending_items) || 0), 0)}</div>
                            <div className="stat-label">Pending Facility Items</div>
                        </div>
                    </div>
                    <div className="stat-box">
                        <div className="stat-icon orange">
                            <i className="fa-solid fa-toolbox"></i>
                        </div>
                        <div>
                            <div className="stat-number">{pendingBookings.reduce((sum, b) => sum + (parseInt(b.pending_equipment) || 0), 0)}</div>
                            <div className="stat-label">Pending Equipment</div>
                        </div>
                    </div>
                </div>
            )}

            {pendingBookings.length === 0 ? (
                <div className="empty-state">
                    <i className="fa-solid fa-check-circle"></i>
                    <h3>All Clear!</h3>
                    <p>No pending reconciliations. All items have been returned.</p>
                </div>
            ) : (
                <>
                    <div className="bookings-grid">
                        {currentBookings.map(booking => (
                            <div key={booking.id} className="booking-card">
                                <div className="card-top">
                                    <div className="booking-id">#{booking.booking_number}</div>
                                    <div className="pending-count">
                                        <i className="fa-solid fa-hourglass-half"></i>
                                        {booking.total_pending || (parseInt(booking.pending_items) + parseInt(booking.pending_equipment))} pending
                                    </div>
                                </div>
                                <div className="facility-name">
                                    <i className="fa-solid fa-building"></i>
                                    {booking.facility_name || 'N/A'}
                                </div>
                                <div className="member-info">
                                    <div className="member-name">
                                        <i className="fa-solid fa-user-circle"></i>
                                        <strong>{booking.full_name || `${booking.first_name} ${booking.last_name}`}</strong>
                                    </div>
                                    <div className="member-contact">
                                        <span><i className="fa-solid fa-envelope"></i> {booking.email}</span>
                                        <span><i className="fa-solid fa-phone"></i> {booking.phone || 'N/A'}</span>
                                    </div>
                                </div>
                                <div className="date-time">
                                    <span><i className="fa-regular fa-calendar"></i> {booking.booking_date ? new Date(booking.booking_date).toLocaleDateString() : 'N/A'}</span>
                                    {booking.start_time && booking.end_time && (
                                        <span><i className="fa-regular fa-clock"></i> {booking.start_time.substring(0,5)} - {booking.end_time.substring(0,5)}</span>
                                    )}
                                </div>
                                <div className="items-breakdown">
                                    {(parseInt(booking.pending_items) > 0) && (
                                        <div className="item-badge facility">
                                            <i className="fa-solid fa-cube"></i>
                                            {booking.pending_items} facility item(s)
                                        </div>
                                    )}
                                    {(parseInt(booking.pending_equipment) > 0) && (
                                        <div className="item-badge equipment">
                                            <i className="fa-solid fa-toolbox"></i>
                                            {booking.pending_equipment} equipment item(s)
                                        </div>
                                    )}
                                </div>
                                <div className="card-actions">
                                    <button 
                                        className="btn-complete"
                                        onClick={() => handleCompleteReconciliation(booking.id)}
                                    >
                                        <i className="fa-solid fa-check-double"></i>
                                        Complete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                <i className="fa-solid fa-chevron-left"></i> Previous
                            </button>
                            <div className="pagination-numbers">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }
                                    return (
                                        <button key={pageNum} className={`pagination-number ${pageNum === currentPage ? 'active' : ''}`} onClick={() => handlePageChange(pageNum)}>
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                            <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                                Next <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </>
            )}
        </>
    );

    const MonthlyReportTab = () => (
        <div className="report-tab">
            <div className="report-header">
                <h2>Monthly Reconciliation Report</h2>
                <div className="report-controls">
                    <YearSelector />
                    <MonthSelector />
                </div>
            </div>
            
            {reportLoading ? (
                <div className="loading-spinner">Loading monthly data...</div>
            ) : monthlyData.length > 0 ? (
                <>
                    <ReconciliationTrendChart data={monthlyData} title="Monthly Reconciliation Trends" color="#8B5CF6" />
                    <ItemsFlowChart data={monthlyData} />
                    <div className="data-table-container">
                        <h3>Monthly Breakdown</h3>
                        <div className="table-responsive">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Month</th><th>Total</th><th>Completed</th><th>Pending</th><th>Items Issued</th><th>Items Returned</th><th>Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {monthlyData.map((month) => (
                                        <tr key={month.month}>
                                            <td className="text-bold">{month.label}</td>
                                            <td>{month.reconciliations}</td>
                                            <td className="text-green">{month.completed}</td>
                                            <td className="text-orange">{month.pending}</td>
                                            <td>{month.itemsIssued}</td>
                                            <td>{month.itemsReturned}</td>
                                            <td><span className={`rate-badge ${month.onTimeRate >= 80 ? 'high' : month.onTimeRate >= 60 ? 'medium' : 'low'}`}>{month.onTimeRate}%</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (
                <div className="empty-state"><i className="fa-solid fa-chart-line"></i><h3>No Data</h3><p>No monthly data available</p></div>
            )}
        </div>
    );

    const QuarterlyReportTab = () => (
        <div className="report-tab">
            <div className="report-header">
                <h2>Quarterly Reconciliation Report</h2>
                <div className="report-controls">
                    <YearSelector />
                    <QuarterSelector />
                </div>
            </div>
            {reportLoading ? (
                <div className="loading-spinner">Loading quarterly data...</div>
            ) : quarterlyData.length > 0 ? (
                <>
                    <ReconciliationTrendChart data={quarterlyData} title="Quarterly Reconciliation Trends" color="#EC4899" />
                    <ItemsFlowChart data={quarterlyData} />
                    <div className="data-table-container">
                        <h3>Quarterly Breakdown</h3>
                        <div className="table-responsive">
                            <table className="data-table">
                                <thead><tr><th>Quarter</th><th>Total</th><th>Completed</th><th>Pending</th><th>Items Issued</th><th>Items Returned</th><th>Rate</th></tr></thead>
                                <tbody>
                                    {quarterlyData.map((quarter) => (
                                        <tr key={quarter.quarter}>
                                            <td className="text-bold">{quarter.label}</td>
                                            <td>{quarter.reconciliations}</td>
                                            <td className="text-green">{quarter.completed}</td>
                                            <td className="text-orange">{quarter.pending}</td>
                                            <td>{quarter.itemsIssued}</td>
                                            <td>{quarter.itemsReturned}</td>
                                            <td><div className="progress-bar"><div className="progress-fill" style={{ width: `${quarter.completionRate}%` }}></div><span>{quarter.completionRate}%</span></div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (
                <div className="empty-state"><i className="fa-solid fa-chart-line"></i><h3>No Data</h3><p>No quarterly data available</p></div>
            )}
        </div>
    );

    const YearlyReportTab = () => (
        <div className="report-tab">
            <div className="report-header">
                <h2>Yearly Reconciliation Report</h2>
            </div>
            {reportLoading ? (
                <div className="loading-spinner">Loading yearly data...</div>
            ) : yearlyData.length > 0 ? (
                <>
                    <ReconciliationTrendChart data={yearlyData} title="Yearly Reconciliation Trends" color="#06B6D4" />
                    <ItemsFlowChart data={yearlyData} />
                    <div className="data-table-container">
                        <h3>Yearly Breakdown</h3>
                        <div className="table-responsive">
                            <table className="data-table">
                                <thead><tr><th>Year</th><th>Total</th><th>Completed</th><th>Pending</th><th>Items Issued</th><th>Items Returned</th><th>YoY Growth</th></tr></thead>
                                <tbody>
                                    {yearlyData.map((year) => (
                                        <tr key={year.year}>
                                            <td className="text-bold">{year.year}</td>
                                            <td>{year.reconciliations}</td>
                                            <td className="text-green">{year.completed}</td>
                                            <td className="text-orange">{year.pending}</td>
                                            <td>{year.itemsIssued}</td>
                                            <td>{year.itemsReturned}</td>
                                            <td>{year.growth > 0 ? <span className="growth-positive">↑ {year.growth}%</span> : year.growth < 0 ? <span className="growth-negative">↓ {Math.abs(year.growth)}%</span> : <span>0%</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (
                <div className="empty-state"><i className="fa-solid fa-chart-bar"></i><h3>No Data</h3><p>No yearly data available</p></div>
            )}
        </div>
    );

    const StatisticsTab = () => (
        <div className="statistics-tab">
            <h2>Reconciliation Statistics & Analytics</h2>
            <StatisticsCards />
            <div className="charts-row">
                <CompletionRatePieChart />
                <div className="chart-container">
                    <h3>Average Processing Time</h3>
                    <div className="metric-large">
                        <div className="metric-value">{reconciliationStats.avgProcessingDays}</div>
                        <div className="metric-label">Days Average Processing Time</div>
                    </div>
                </div>
            </div>
            <EquipmentStatsChart />
            <ReconciliationDataTable />
            <div className="export-actions">
                <button className="export-btn" onClick={() => { const csv = convertToCSV(allReconciliations); downloadCSV(csv, `reconciliations_${new Date().toISOString().split('T')[0]}.csv`); }}>
                    <i className="fa-solid fa-file-csv"></i> Export CSV
                </button>
                <button className="export-btn" onClick={() => { const json = JSON.stringify(allReconciliations, null, 2); downloadJSON(json, `reconciliations_${new Date().toISOString().split('T')[0]}.json`); }}>
                    <i className="fa-solid fa-file-json"></i> Export JSON
                </button>
                <button className="print-btn" onClick={() => window.print()}>
                    <i className="fa-solid fa-print"></i> Print Report
                </button>
            </div>
        </div>
    );

const SectionsTab = () => (
    <div className="sections-tab">
        <h2>Section Management</h2>
        <SectionStatsCards />
        <div className="charts-row">
            <SectionMembershipChart />
            <div className="chart-container">
                <h3>Pending Section Requests</h3>
                <div className="metric-large">
                    <div className="metric-value">{sectionRequests.length}</div>
                    <div className="metric-label">Requests Awaiting Approval</div>
                </div>
            </div>
        </div>
        <div className="data-table-container">
            <h3>Your Section Memberships</h3>
            <div className="table-responsive">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Section Name</th>
                            <th>Request Date</th>
                            <th>Status</th>
                            <th>Role</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sectionMemberships.map((membership) => (
                            <tr key={membership.id}>
                                <td>{membership.section_name}</td>
                                <td>{new Date(membership.created_at).toLocaleDateString()}</td>
                                <td>
                                    <span className={`status-badge ${membership.status === 'approved' ? 'completed' : 'pending'}`}>
                                        {membership.status}
                                    </span>
                                </td>
                                <td>{membership.role || 'Member'}</td>
                            </tr>
                        ))}
                        {sectionMemberships.length === 0 && (
                            <tr>
                                <td colSpan="4" className="text-center">No section memberships found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);
    // ============================================
    // MAIN RENDER
    // ============================================
    if (loading && pendingBookings.length === 0) {
        return (
            <div className="reconciliation-loading">
                <div className="spinner"></div>
                <p>Loading pending reconciliations...</p>
            </div>
        );
    }

    return (
        <div className="admin-reconciliation">
            <div className="reconciliation-header">
                <div>
                    <h1 className="page-title">Reconciliation Management</h1>
                    <p className="page-subtitle">Review pending reconciliations, track returns, and analyze reconciliation data</p>
                </div>
                <button onClick={() => { fetchPendingReconciliations(); fetchAllReportData(); fetchAvailableYears(); fetchSectionData(); }} className="refresh-button">
                    <i className="fa-solid fa-rotate-right"></i> Refresh All Data
                </button>
            </div>

            <TabNavigation />
            <div className="tab-content">
                {activeTab === 'pending' && <PendingReconciliationsTab />}
                {activeTab === 'monthly' && <MonthlyReportTab />}
                {activeTab === 'quarterly' && <QuarterlyReportTab />}
                {activeTab === 'yearly' && <YearlyReportTab />}
                {activeTab === 'statistics' && <StatisticsTab />}
                {activeTab === 'sections' && <SectionsTab />}
            </div>

            {showIssueModal && selectedBooking && (
                <div className="modal-overlay" onClick={() => setShowIssueModal(false)}>
                    <div className="issue-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Issue Items</h3>
                            <button className="close-btn" onClick={() => setShowIssueModal(false)}><i className="fa-solid fa-times"></i></button>
                        </div>
                        <div className="modal-body">
                            <div className="booking-info-modal">
                                <div className="info-row"><span className="info-label">Booking:</span><span className="info-value">#{selectedBooking.booking_number}</span></div>
                                <div className="info-row"><span className="info-label">Member:</span><span className="info-value">{selectedBooking.full_name}</span></div>
                                <div className="info-row"><span className="info-label">Facility:</span><span className="info-value">{selectedBooking.facility_name}</span></div>
                                <div className="info-row"><span className="info-label">Date:</span><span className="info-value">{new Date(selectedBooking.booking_date).toLocaleDateString()}</span></div>
                            </div>
                            <div className="equipment-section">
                                <h4>Select Items to Issue</h4>
                                <div className="equipment-list">
                                    {availableEquipment.filter(eq => eq.available > 0).map(eq => (
                                        <label key={eq.id} className="equipment-option">
                                            <input type="checkbox" checked={selectedItems.some(i => i.id === eq.id)} onChange={() => {
                                                if (selectedItems.some(i => i.id === eq.id)) {
                                                    setSelectedItems(selectedItems.filter(i => i.id !== eq.id));
                                                } else {
                                                    setSelectedItems([...selectedItems, { id: eq.id, name: eq.name, type: 'equipment', quantity: 1 }]);
                                                }
                                            }} />
                                            <span className="equipment-name">{eq.name}</span>
                                            <span className="equipment-available">Available: {eq.available}</span>
                                        </label>
                                    ))}
                                </div>
                                {availableEquipment.filter(eq => eq.available > 0).length === 0 && <p className="no-equipment">No equipment available to issue.</p>}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowIssueModal(false)}>Cancel</button>
                            <button className="btn-submit" onClick={handleIssueItems} disabled={issuing || selectedItems.length === 0}>
                                {issuing ? 'Issuing...' : `Issue ${selectedItems.length} Item(s)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminReconciliation;